import { ReactNode } from 'react';
import styled from 'styled-components';

import { simpleDateAtTime } from 'lib/date';
import Workflow, { WorkflowAction, WorkflowGroupOptions } from 'models/Workflow';
import { colors } from 'styles/theme/colors';
import { BodySmall, BodySmallBold } from 'styles/typography';

import { ActionKindMeta } from './actionConfig';
import { actionStatusDisplay } from './StatusBadge';
import StatusIcon, { indicatorColor, statusIndicator } from './StatusIcon';

// The generic panel for a fanned-out (per-building) step type (OLIO-2611): one row per
// building, with the step type's own detail plugged in via renderRow. Deliberately
// minimal — kind-specific mechanics (Facility Decisions' search/select-all/bulk) live
// in that kind's own panel, not here.
interface Props {
  actions: WorkflowAction[];
  workflow: Workflow;
  renderRow: (action: WorkflowAction) => ReactNode;
  // The explanatory line above the rows (the step's API description) — where Facility
  // Decisions puts its search toolbar, per the Figma.
  blurb?: string | null;
  // Right-aligned content on the row's header line (e.g. the assessment View link).
  aside?: (action: WorkflowAction) => ReactNode;
}

// The building label for a fanned instance.
export function buildingName(workflow: Workflow, action: WorkflowAction): string {
  return workflow.groups.find((group) => group.id === action.groupId)?.name ?? 'Unknown building';
}

// Every per-building list reads alpha by building name — one comparator so the panels,
// the outcome rows, and the winner dropdown can never disagree on order.
export function sortedByBuildingName(workflow: Workflow, actions: WorkflowAction[]): WorkflowAction[] {
  return [...actions].sort((a, b) => buildingName(workflow, a).localeCompare(buildingName(workflow, b)));
}

export function sortedGroupsByName(groups: WorkflowGroupOptions[]): WorkflowGroupOptions[] {
  return [...groups].sort((a, b) => a.name.localeCompare(b.name));
}

// "Updated by <name> on <date>" — the designed attribution line on decided rows and
// outcome rows; null until the action completes.
export function completionAttribution(action: WorkflowAction): string | null {
  if (!action.completedAt) return null;
  const by = action.completedByName ? ` by ${action.completedByName}` : '';
  return `Updated${by} on ${simpleDateAtTime(action.completedAt)}`;
}

// The header rollup for a fanned kind — "2 Accepted • 1 Denied • 1 Pending". Completed
// rows count under their recorded result (meta.countLabels); everything else under its
// status label.
export function fanoutCounts(workflow: Workflow, actions: WorkflowAction[], meta: ActionKindMeta | undefined): string {
  const counts = new Map<string, number>();
  actions.forEach((action) => {
    const result = meta?.resultValueName ? workflow.valueFor(meta.resultValueName, action.groupId)?.value : undefined;
    // An undecided-but-actionable row rolls up as "Pending" (matching its "Pending
    // Decision" subtitle) — "Ready for Review" is the card badge's vocabulary, not the
    // building counts'.
    const label =
      (action.isCompleted && typeof result === 'string' && meta?.countLabels?.[result]) ||
      (action.isActionRequired ? 'Pending' : actionStatusDisplay(action.status).label);
    counts.set(label, (counts.get(label) ?? 0) + 1);
  });
  return Array.from(counts, ([label, count]) => `${count} ${label}`).join(' • ');
}

export default function FanoutPanel({ actions, workflow, renderRow, blurb, aside }: Props) {
  const ordered = sortedByBuildingName(workflow, actions);

  return (
    <Panel data-testid='fanout-panel'>
      {blurb && <Blurb data-testid='fanout-blurb'>{blurb}</Blurb>}
      {ordered.map((action) => (
        <Row key={action.id} data-testid='fanout-row' data-group-id={action.groupId ?? undefined}>
          <StatusIcon indicator={statusIndicator(action)} size={16} />
          <RowMain>
            <RowHeader>
              <RowName>{buildingName(workflow, action)}</RowName>
              <RowStatus $color={indicatorColor(statusIndicator(action))}>
                {actionStatusDisplay(action.status).label}
              </RowStatus>
              {aside && <RowAside>{aside(action)}</RowAside>}
            </RowHeader>
            {renderRow(action)}
          </RowMain>
        </Row>
      ))}
    </Panel>
  );
}

const Panel = styled.div`
  display: flex;
  flex-direction: column;
`;

const Blurb = styled(BodySmall)`
  padding: 12px 16px;
  color: ${colors.black75};
  border-bottom: 1px solid ${colors.black5};
`;

const Row = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 12px;
  padding: 14px 16px;

  & + & {
    border-top: 1px solid ${colors.black5};
  }

  > input {
    margin-top: 3px;
  }

  > span {
    margin-top: 2px;
  }
`;

const RowMain = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  min-width: 0;
  flex: 1;
`;

const RowHeader = styled.div`
  display: flex;
  align-items: baseline;
  gap: 10px;
`;

const RowAside = styled.span`
  margin-left: auto;
`;

const RowName = styled(BodySmallBold)`
  font-size: 14px;
`;

const RowStatus = styled(BodySmall)<{ $color?: string }>`
  color: ${({ $color }) => $color ?? colors.black50};
`;
